import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import mixpanel from 'mixpanel-browser';
import { useParams, NavLink } from 'react-router-dom';

import { post } from '../../../../services/BackendService';
import InquiryFormComponent from '../../../../components/InquiryFormComponent';
import FaqComponent from '../../../../components/FaqComponent';
import faqsData from '../../../../data/faqs.json';
import './ExperienceItemPage.scss';

// Dynamically import experience data based on the route parameter
const importExperienceData = async (slug) => {
    try {
        const experienceData = await import(`../../../../data/experiences/${slug}.json`);
        return experienceData.default;
    } catch (error) {
        console.error('Failed to load experience data:', error);
        return null;
    }
};

// Dynamically import experience metadata from experiences.json
const findExperienceMetadata = async (slug) => {
    try {
        const experiences = await import('../../../../data/experiences.json');
        return experiences.default.find(exp => exp.link === `/experiences/${slug}`);
    } catch (error) {
        console.error('Failed to load experience metadata:', error);
        return null;
    }
};

export default function ExperiencePage() {
    const { experience } = useParams(); // Get the slug from route parameters
    const [tours, setTours] = useState([]);
    const [experienceMetadata, setExperienceMetadata] = useState(null);
    const [faqData, setFaqData] = useState(null);
    const [contactForm, setContactForm] = useState({
        full_name: '',
        email_address: '',
        phone_number: '',
        message: 'Hello, I am interested in this experience.',
    });
    const [sent, setSent] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Load tours and metadata for the current experience
        setLoading(true);
        Promise.all([
            importExperienceData(experience),
            findExperienceMetadata(experience)
        ]).then(([loadedTours, metadata]) => {
            if (loadedTours) {
                setTours(loadedTours);
                mixpanel.track(`Visited ${experience} page`);
            } else {
                setError('No tours found for this experience');
            }
            
            if (metadata) {
                setExperienceMetadata(metadata);
            }
            setLoading(false);
        }).catch(() => {
            setError('Failed to load experience data');
            setLoading(false);
        });

        // Find the experiences FAQs
        const experiencesFaqs = faqsData.find(faq => faq.id === "experiences");
        setFaqData(experiencesFaqs);
    }, [experience]);

    const handleContactFormChange = ({target}) => {
        setContactForm(prevForm => ({
            ...prevForm,
            [target.id]: target.value
        }));
    }

    const submitContactForm = (event) => {
        event.preventDefault();
        
        if (event.target.checkValidity()) {
            event.target.classList.remove('was-validated');

            mixpanel.identify(contactForm.email_address);
            mixpanel.people.set({
                $name: contactForm.full_name,
                $email: contactForm.email_address,
            });

            post('/sendEmail', {
                ...contactForm,
                booking_reference: experienceMetadata?.title || 'Experience Inquiry'
            })
                .then(response => {
                    setSent(true);
                    setContactForm({
                        full_name: '',
                        email_address: '',
                        phone_number: '',
                        message: '',
                        booking_reference: experienceMetadata?.title || 'Experience Inquiry'
                    });
                })
                .catch((error) => {
                    setError(error.response?.data?.error?.message || 'An error occurred');
                });
        } else {
            event.target.classList.add('was-validated');
        }
    }

    // Generate structured data for SEO
    const generateStructuredData = () => {
        if (!experienceMetadata) return null;
        
        const tourItems = tours.map((tour, index) => ({
            "@type": "TouristAttraction",
            "name": tour.title,
            "description": tour.description,
            "image": `https://pickuphungary.com${tour.image}`,
            "url": tour.link,
            "offers": {
                "@type": "Offer",
                "price": tour.price ? tour.price.replace(/[^\d.]/g, '') : "",
                "priceCurrency": "EUR"
            }
        }));
        
        return {
            "@context": "https://schema.org",
            "@type": "TouristTrip",
            "name": experienceMetadata.title,
            "description": experienceMetadata.description,
            "image": `https://pickuphungary.com${experienceMetadata.image}`,
            "touristAttraction": tourItems
        };
    };

    return (
        <div className="experiences-subcategory">
            <Helmet>
                <title>{experienceMetadata?.title || 'Experience'} | Pickup Hungary</title>
                <meta
                    name="description"
                    content={experienceMetadata?.description || 'Explore unique experiences with Pickup Hungary'}
                />
                <meta 
                    name="keywords" 
                    content={`${experienceMetadata?.title || 'Hungary experiences'}, tours, activities, Pickup Hungary, ${experience?.replace(/-/g, ' ')}`} 
                />
                <link rel="canonical" href={`https://pickuphungary.com/experiences/${experience}`} />
                {experienceMetadata && (
                    <script type="application/ld+json">
                        {JSON.stringify(generateStructuredData())}
                    </script>
                )}
            </Helmet>

            <div className="container-fluid p-0">
                <div className="experiences-subcategory-hero position-relative">
                    <img 
                        src={experienceMetadata?.image || "/images/activities/cruise-budapest.jpg"} 
                        alt={experienceMetadata?.title || "Experience"} 
                        className="img-fluid w-100"
                    />
                    <div className="experiences-subcategory-hero-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center">
                        <div className="text-center text-white">
                            <h1>{experienceMetadata?.title || 'Experience Details'}</h1>
                            {experienceMetadata && <p className="hero-subtitle">Discover the best of {experienceMetadata.title}</p>}
                        </div>
                    </div>
                </div>
            </div>

            <nav aria-label="breadcrumb" className="container mt-3">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item"><NavLink to="/">Home</NavLink></li>
                    <li className="breadcrumb-item"><NavLink to="/experiences">Experiences</NavLink></li>
                    <li className="breadcrumb-item active" aria-current="page">
                        {experienceMetadata?.title || experience?.replace(/-/g, ' ')}
                    </li>
                </ol>
            </nav>

            {error && <Alert variant="danger" className="container mt-3">{error}</Alert>}
            
            {loading ? (
                <div className="container py-5 text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            ) : (
                <>
                    <section className="experience-intro">
                        <div className="container">
                            <div className="row justify-content-center">
                                <div className="col-lg-8 text-center">
                                    <h2>{experienceMetadata?.title || 'Explore Our Experiences'}</h2>
                                    <p className="lead">{experienceMetadata?.description || 'Discover unique experiences with Pickup Hungary'}</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="tours-section">
                        <div className="container">
                            {tours.map((tour, index) => (
                                <div key={tour.id} className="tour-card">
                                    <div className="row g-0">
                                        <div className="col-md-5">
                                            <div className="tour-image-container">
                                                <a href={tour.link} target="_blank" rel="noreferrer">
                                                    <img 
                                                        src={tour.image} 
                                                        alt={tour.title} 
                                                        className="img-fluid rounded-start experience-image"
                                                    />
                                                    <div className="tour-image-overlay">
                                                        <span>View Details</span>
                                                    </div>
                                                </a>
                                            </div>
                                        </div>
                                        <div className="col-md-7">
                                            <div className="tour-content">
                                                <h2>
                                                    <a href={tour.link} className="tour-title" target="_blank" rel="noreferrer">
                                                        {tour.title}
                                                    </a>
                                                </h2>
                                                
                                                <div className="tour-meta">
                                                    {tour.duration && (
                                                        <div className="tour-duration">
                                                            <i className="fa fa-clock-o me-2"></i>
                                                            {tour.duration}
                                                        </div>
                                                    )}
                                                    {tour.price && (
                                                        <div className="tour-price">
                                                            <i className="fa fa-tag me-2"></i>
                                                            {tour.price}
                                                        </div>
                                                    )}
                                                </div>
                                                
                                                <p className="tour-description">{tour.description}</p>
                                                
                                                <a 
                                                    href={tour.link} 
                                                    target="_blank" 
                                                    className="btn btn-primary" 
                                                    rel="noreferrer" 
                                                    onClick={() => mixpanel.track('Learn More Clicked', { 
                                                        experience_title: tour.title 
                                                    })}>
                                                    Learn More
                                                    <i className="fa fa-chevron-right ms-2"></i>
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                </>
            )}

            <InquiryFormComponent 
                title={`Ready to Explore ${experienceMetadata?.title || 'Hungary'}?`}
                subtitle="Book your personalized experience today!"
                features={[
                    "Expert local guides",
                    "Customizable experiences",
                    "Comfortable transportation",
                    "Flexible scheduling"
                ]}
                defaultMessage="Hello, I am interested in this experience."
                bookingReference={experienceMetadata?.title || 'Experience Inquiry'}
                includeWhatsApp={true}
                section="experience-items"
            />
            
            {/* FAQ Section */}
            {faqData && (
                <FaqComponent 
                    title={faqData.title}
                    faqs={faqData.faqs}
                />
            )}

            <section className="related-experiences">
                <div className="container text-center">
                    <h2>Explore More Experiences</h2>
                    <p className="lead mb-4">Discover other amazing activities in Hungary</p>
                    <div className="text-center mt-4">
                        <NavLink to="/experiences" className="btn btn-outline-primary btn-lg text-white">
                            View All Experiences
                        </NavLink>
                    </div>
                </div>
            </section>
        </div>
    );
}
